import type { ConnectionResponse, ConnectionQuery, Connection } from '../types/connections';
import { VL_API_BASE_URL } from '../constants/backendUrls';
import { APIClient } from './apiClient';

const baseUrl = `${VL_API_BASE_URL}/connections`;

export async function loadConnections (queryParam?: ConnectionQuery, fetchOptions?: Record<string, unknown>) {
    let search = '';
    if (queryParam?.environmentId) {
        search = `?search=environmentId="${queryParam.environmentId}"`;
    } else if (queryParam?.system) {
        search = `?search=system="${queryParam.system}"`;
    } else if (queryParam?.label) {
        search = `?search=labels="${queryParam.label}"`;
    }

    const url = search.length > 0 ? `${baseUrl}${search}` : baseUrl;
    return await APIClient<ConnectionResponse>(url, fetchOptions);
}


export async function loadConnectionsByEnvironmentId (environmentId: string) {
    return await loadConnections({ environmentId });
}

export async function loadConnectionsByEnvironmentIds (environmentIds: string[], fetchOptions?: Record<string, unknown>) {
    const uniqueIds = [...new Set(environmentIds.filter(Boolean))];
    if (!uniqueIds.length) {
        return { items: [], count: 0 } as ConnectionResponse;
    }

    const idSet = new Set(uniqueIds);
    const response = await loadConnections(undefined, fetchOptions);
    const items = response.items.filter(connection => connection.environmentId && idSet.has(connection.environmentId));
    return {
        ...response,
        items,
        count: items.length
    };
}

export async function fetchAllConnections () {
    return await APIClient<ConnectionResponse>(baseUrl);
}

export async function fetchConnectionDetails (connectionId: string) {
    return await APIClient<Connection>(`${baseUrl}/${connectionId}`);
}

export async function addNewConnection (payload: Connection) {
    return await APIClient<Connection>(baseUrl, { method: 'POST', body: JSON.stringify(payload) });
}

export async function editConnection (payload: Connection) {
    return await APIClient<Connection>(`${baseUrl}/${payload.connectionId}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function deleteConnection (connectionId: string) {
    return await APIClient<Connection>(`${baseUrl}/${connectionId}`, { method: 'DELETE' });
}

export async function verifyConnection (connectionId: string) {
    return await APIClient<string>(`${baseUrl}/${connectionId}/verify`);
}

export async function issueCertificate (connectionId: string) {
    return await APIClient<null>(`${baseUrl}/${connectionId}/issue-certificate`, { method: 'POST', body: {} });
}

export async function revokeCertificate (connectionId: string, tenantId: string) {
    const payload = {
        connectionId,
        tenantId,
        certificate: {}
    };
    return await APIClient<Connection>(`${baseUrl}/${connectionId}`, { method: 'PATCH', body: JSON.stringify(payload) });
}

export async function fetchWorkflowsWithConnection (connectionId: string) {
    return await APIClient(`${VL_API_BASE_URL}/v1/workflows/?search=${encodeURIComponent(`"${connectionId}"`)}&limit=5`);
}
